import _get from 'lodash/get';
import {
  fhirResourceToDateFilter,
  ResourceDateFilter,
} from '../../util/configuredResourceTypes';
import {
  DateFilter,
  PatientFilter,
  SubscriberFilter,
  TagFilter,
  TagFilterNotCodeValue,
  PageSizeFilter,
  ParameterSet,
  CodeFilter,
  CategoryFilter,
  Sort,
} from '../../util/PatientServiceParameters';
import Parameter from '../../util/PatientServiceParameters/Parameter';
import { SupportedResourceTypes, Code } from '../../util/types';
import { SearchConfig } from '.';
import { PatientFilterFhirProp } from '../../util/PatientServiceParameters/PatientFilter';
import { ResolveTerminologyCodeId } from '../../util/PatientServiceParameters/CodeFilter';
import DeviceFilter from '../../util/PatientServiceParameters/DeviceFilter';

export const getDateFilter = (
  resourceType: SupportedResourceTypes,
  searchConfig: SearchConfig = {} as SearchConfig,
): ResourceDateFilter => {
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  if (!resourceType) return null;

  const configuredDateFilterProperty = _get(
    searchConfig.timeFilterProps,
    resourceType,
  );

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  return (
    fhirResourceToDateFilter(
      resourceType,
      configuredDateFilterProperty || null,
    ) || null
  );
};

const getDateFilters = (
  resourceType: SupportedResourceTypes,
  searchConfig: SearchConfig = {} as SearchConfig,
): Parameter[] => {
  const dateFilter = getDateFilter(resourceType, searchConfig);
  if (!dateFilter) return [];

  return [
    new DateFilter(
      dateFilter,
      searchConfig.dateBounds,
      searchConfig.applyDateFilter,
    ),
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    new Sort(dateFilter.property, searchConfig.applyDateFilter, true),
  ];
};

type DefaultFilterOptions = {
  patientFhirProp: PatientFilterFhirProp;
};

const getDefaultFilters = (
  resourceType: SupportedResourceTypes,
  searchConfig: SearchConfig = {} as SearchConfig,
  { patientFhirProp }: DefaultFilterOptions,
): Parameter[] => [
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  new PatientFilter(searchConfig.patientId, patientFhirProp),
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  new TagFilter(searchConfig.useTagFilter, searchConfig.tagFilters),
  new TagFilterNotCodeValue(
    searchConfig.useNotTagFilter,
    searchConfig.notTagFilters,
  ),
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  new PageSizeFilter(searchConfig.pageSize),
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  new CategoryFilter(searchConfig.useCategoryFilter, searchConfig.categories),
  ...getDateFilters(resourceType, searchConfig),
];

export type ResourceTypeToParameterGeneration = {
  [key in SupportedResourceTypes]: (
    searchConfig: SearchConfig,
    resolveTerminologyCodeId: ResolveTerminologyCodeId,
  ) => ParameterSet;
};

export type MedicationResourceTypes =
  | 'MedicationStatement'
  | 'MedicationAdministration'
  | 'MedicationRequest'
  | 'MedicationDispense';

const getMedicationRelatedParams = (
  resourcetype: MedicationResourceTypes,
  searchConfig: SearchConfig,
  resolveTerminologyCodeId: ResolveTerminologyCodeId,
): Parameter[] => {
  const toParams = (codes: Code[], displayAll: boolean) => [
    new CodeFilter(
      codes,
      displayAll,
      'code',
      'medication',
      resolveTerminologyCodeId,
    ),
  ];

  if (searchConfig.dataSource === 'MEDICATION_DATA_SOURCE_TYPE') {
    return toParams(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      searchConfig.medicationCodes,
      searchConfig.displayAllMedicationCodes,
    );
  }

  switch (resourcetype) {
    case 'MedicationAdministration':
      return toParams(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        searchConfig.medicationAdministrationCodes,
        searchConfig.displayAllMedicationAdministrationCodes,
      );
    case 'MedicationRequest':
      return toParams(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        searchConfig.medicationRequestCodes,
        searchConfig.displayAllMedicationRequestCodes,
      );
    case 'MedicationStatement':
      return toParams(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        searchConfig.medicationStatementCodes,
        searchConfig.displayAllMedicationStatementCodes,
      );
    case 'MedicationDispense':
      return toParams(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        searchConfig.medicationDispenseCodes,
        searchConfig.displayAllMedicationDispenseCodes,
      );
  }
};

const resourceTypeToParameterGeneration: ResourceTypeToParameterGeneration = {
  Observation: (
    searchConfig: SearchConfig,
    resolveTerminologyCodeId: ResolveTerminologyCodeId,
  ) => {
    const codeFilter = new CodeFilter(
      searchConfig.observationCodes,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      searchConfig.displayAllObservationCodes,
      'code',
      'code',
      resolveTerminologyCodeId,
    );
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    const deviceFilter = new DeviceFilter(searchConfig.deviceId);

    return new ParameterSet([
      ...getDefaultFilters('Observation', searchConfig, {
        patientFhirProp: 'subject',
      }),
      codeFilter,
      deviceFilter,
    ]);
  },
  MedicationStatement: (
    searchConfig: SearchConfig,
    resolveTerminologyCodeId: ResolveTerminologyCodeId,
  ) => {
    return new ParameterSet([
      ...getDefaultFilters('MedicationStatement', searchConfig, {
        patientFhirProp: 'subject',
      }),
      ...getMedicationRelatedParams(
        'MedicationStatement',
        searchConfig,
        resolveTerminologyCodeId,
      ),
    ]);
  },
  MedicationAdministration: (
    searchConfig: SearchConfig,
    resolveTerminologyCodeId: ResolveTerminologyCodeId,
  ) => {
    return new ParameterSet([
      ...getDefaultFilters('MedicationAdministration', searchConfig, {
        patientFhirProp: 'subject',
      }),
      ...getMedicationRelatedParams(
        'MedicationAdministration',
        searchConfig,
        resolveTerminologyCodeId,
      ),
    ]);
  },
  MedicationRequest: (
    searchConfig: SearchConfig,
    resolveTerminologyCodeId: ResolveTerminologyCodeId,
  ) => {
    return new ParameterSet([
      ...getDefaultFilters('MedicationRequest', searchConfig, {
        patientFhirProp: 'subject',
      }),
      ...getMedicationRelatedParams(
        'MedicationRequest',
        searchConfig,
        resolveTerminologyCodeId,
      ),
    ]);
  },
  MedicationDispense: (
    searchConfig: SearchConfig,
    resolveTerminologyCodeId: ResolveTerminologyCodeId,
  ) => {
    return new ParameterSet([
      ...getDefaultFilters('MedicationDispense', searchConfig, {
        patientFhirProp: 'subject',
      }),
      ...getMedicationRelatedParams(
        'MedicationDispense',
        searchConfig,
        resolveTerminologyCodeId,
      ),
    ]);
  },
  Procedure: (
    searchConfig: SearchConfig,
    resolveTerminologyCodeId: ResolveTerminologyCodeId,
  ) => {
    const codeFilter = new CodeFilter(
      searchConfig.procedureCodes,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      searchConfig.displayAllProcedureCodes,
      'code',
      'code',
      resolveTerminologyCodeId,
    );
    return new ParameterSet([
      ...getDefaultFilters('Procedure', searchConfig, {
        patientFhirProp: 'subject',
      }),
      codeFilter,
    ]);
  },
  Encounter: (
    searchConfig: SearchConfig,
    resolveTerminologyCodeId: ResolveTerminologyCodeId,
  ) => {
    const codeFilter = new CodeFilter(
      searchConfig.encounterCodes,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      searchConfig.displayAllEncounterCodes,
      'code',
      'type',
      resolveTerminologyCodeId,
    );
    return new ParameterSet([
      ...getDefaultFilters('Encounter', searchConfig, {
        patientFhirProp: 'subject',
      }),
      codeFilter,
    ]);
  },
  Condition: (
    searchConfig: SearchConfig,
    resolveTerminologyCodeId: ResolveTerminologyCodeId,
  ) => {
    const codeFilter = new CodeFilter(
      searchConfig.conditionCodes,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      searchConfig.displayAllConditionCodes,
      'code',
      'code',
      resolveTerminologyCodeId,
    );
    return new ParameterSet([
      ...getDefaultFilters('Condition', searchConfig, {
        patientFhirProp: 'subject',
      }),
      codeFilter,
    ]);
  },
  Media: (searchConfig: SearchConfig) => {
    return new ParameterSet(
      getDefaultFilters('Media', searchConfig, {
        patientFhirProp: 'subject',
      }),
    );
  },
  DocumentReference: (searchConfig: SearchConfig) => {
    return new ParameterSet(
      getDefaultFilters('DocumentReference', searchConfig, {
        patientFhirProp: 'subject',
      }),
    );
  },
  DiagnosticReport: (
    searchConfig: SearchConfig,
    resolveTerminologyCodeId: ResolveTerminologyCodeId,
  ) => {
    const codeFilter = new CodeFilter(
      searchConfig.diagnosticReportCodes,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      searchConfig.displayAllDiagnosticReportCodes,
      'code',
      'code',
      resolveTerminologyCodeId,
    );
    return new ParameterSet([
      ...getDefaultFilters('DiagnosticReport', searchConfig, {
        patientFhirProp: 'subject',
      }),
      codeFilter,
    ]);
  },
  ReferralRequest: (searchConfig: SearchConfig) => {
    return new ParameterSet(
      getDefaultFilters('ReferralRequest', searchConfig, {
        patientFhirProp: 'subject',
      }),
    );
  },
  Specimen: (searchConfig: SearchConfig) => {
    return new ParameterSet(
      getDefaultFilters('Specimen', searchConfig, {
        patientFhirProp: 'subject',
      }),
    );
  },
  ImagingStudy: (searchConfig: SearchConfig) => {
    return new ParameterSet(
      getDefaultFilters('ImagingStudy', searchConfig, {
        patientFhirProp: 'patient',
      }),
    );
  },
  Consent: (searchConfig: SearchConfig) => {
    return new ParameterSet(
      getDefaultFilters('Consent', searchConfig, {
        patientFhirProp: 'patient',
      }),
    );
  },
  ProcedureRequest: (searchConfig: SearchConfig) => {
    return new ParameterSet(
      getDefaultFilters('ProcedureRequest', searchConfig, {
        patientFhirProp: 'subject',
      }),
    );
  },
  Coverage: (searchConfig: SearchConfig) => {
    return new ParameterSet([
      ...getDefaultFilters('Coverage', searchConfig, {
        patientFhirProp: 'subject',
      }),
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      new SubscriberFilter(searchConfig.patientId),
    ]);
  },
  ExplanationOfBenefit: (searchConfig: SearchConfig) => {
    return new ParameterSet(
      getDefaultFilters('ExplanationOfBenefit', searchConfig, {
        patientFhirProp: 'patient',
      }),
    );
  },
  Sequence: (searchConfig: SearchConfig) => {
    return new ParameterSet([
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      new PatientFilter(searchConfig.patientId, 'patient'),
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      new PageSizeFilter(searchConfig.pageSize),
    ]);
  },
  Goal: (
    searchConfig: SearchConfig,
    resolveTerminologyCodeId: ResolveTerminologyCodeId,
  ) => {
    const codeFilter = new CodeFilter(
      searchConfig.goalCodes,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      searchConfig.displayAllGoalCodes,
      'measure',
      'target.measure',
      resolveTerminologyCodeId,
    );
    return new ParameterSet([
      ...getDefaultFilters('Goal', searchConfig, {
        patientFhirProp: 'subject',
      }),
      codeFilter,
    ]);
  },
  Immunization: (searchConfig: SearchConfig) => {
    return new ParameterSet([
      ...getDefaultFilters('Immunization', searchConfig, {
        patientFhirProp: 'patient',
      }),
    ]);
  },
};

export default (
  resourceType: SupportedResourceTypes,
  searchConfig: SearchConfig,
  resolveTerminologyCodeId: ResolveTerminologyCodeId,
): ParameterSet =>
  resourceTypeToParameterGeneration[resourceType](
    searchConfig,
    resolveTerminologyCodeId,
  );
